import type { CSSProperties, FC, ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';

import { useTranslation } from '../../api/useTranslation';
import type { WinnersTotal, WinnerType } from '../../store/slices/game/types';
import { useIsSmallerThanWide } from '../../styles/hooks/media';
import { Currency } from '../currency/Currency';
import { Icon } from '../icons/icon';

import styles from './winnersList.module.css';

export interface WinnersListProps {
  winners: WinnerType[];
  winnersTotal: WinnersTotal | null;
  currentUser: string;
  currency: string;
  locale: string;
  hideCurrency?: boolean;
  isPaused?: boolean; // used in game help and storybook
  playAreaSize?: number;
  gameHeaderOverlayHeight?: number;
  isPassive?: boolean;
}

export const WinnersList: FC<WinnersListProps> = ({
  winnersTotal,
  winners,
  locale,
  currency,
  currentUser,
  hideCurrency,
  isPaused = false,
  playAreaSize,
  gameHeaderOverlayHeight,
  isPassive,
}): ReactElement | null => {
  const winnersListSlider = useRef<HTMLDivElement>(null);
  const [parentHeight, setParentHeight] = useState(0);
  const [parentWidth, setParentWidth] = useState(0);
  const [scrollWidth, setScrollWidth] = useState(0);
  const [animationEnded, setAnimationEnded] = useState(false);
  const timerIdRef = useRef<number | undefined>(undefined);

  const isSmallerThanWide = useIsSmallerThanWide();
  const isSmallerThanWideRef = useRef(false);

  useEffect(() => {
    isSmallerThanWideRef.current = isSmallerThanWide;
  }, [isSmallerThanWide]);

  const { translations } = useTranslation();
  const winnersListLabel = winnersTotal ? `${winnersTotal?.winnersCount}\u00a0${translations['WINNERLIST']}:`:'';

  useEffect(() => {
    setAnimationEnded(false);

    if (winnersListSlider.current && winnersListSlider.current.parentElement) {
      const parentElement = winnersListSlider.current.parentElement;
      const pw = parentElement.clientWidth;
      const sw = winnersListSlider.current.scrollWidth;

      setParentWidth(pw);
      setParentHeight(parentElement.clientHeight);
      setScrollWidth(sw);

      if (isSmallerThanWideRef.current && !isPaused) {
        const time = ((sw + pw) / 120) * 1000;
        timerIdRef.current = window.setTimeout(() => {
          setAnimationEnded(true);
        }, time);
      }
    }
    return () => {
      if (timerIdRef.current) {
        window.clearTimeout(timerIdRef.current);
      }
    };
  }, [isPaused, winners]);

  if (winners.length <= 0 || animationEnded) return null;
  return (
    <div
      data-testid="winners-list"
      className={`${styles.winnersList}${isPassive ? ` ${styles.winnersList__slider_paused}` : ''}`}
      aria-label={winnersListLabel}
      style={
        {
          '--playAreaSize': `${playAreaSize}px`,
          '--gameHeaderOverlayHeight': `${gameHeaderOverlayHeight}px`,
        } as CSSProperties
      }
      key="Winners"
    >
      <div className={styles.winnersList__sliderCnt}>
        <div
          className={`${styles.winnersList__slider}${isPaused ? ` ${styles.winnersList__slider_paused}` : ``}`}
          ref={winnersListSlider}
          style={
            {
              '--parentHeight': parentHeight,
              '--scrollWidth': scrollWidth,
              '--parentWidth': parentWidth,
              '--animationDuration': 7 + winners.length / 2,
              '--animationFade': 4 + winners.length / 2,
            } as CSSProperties
          }
        >
          {winnersTotal && (
            <h3 className={styles.winnersList__total}>
              <Icon icon={'Player'} />
              {` ${winnersTotal.winnersCount} ${translations['WON']} `}
              <Currency
                locale={locale}
                value={+winnersTotal.totalEur}
                currency={currency}
                hideCurrency={hideCurrency}
                avoidTrailingZero={+winnersTotal.totalEur > 100}
              />
            </h3>
          )}
          {winners.map((winner, index) => {
            const isCurrentUser = winner.userId === currentUser;
            return (
              <div
                key={index}
                className={`${styles.winnersList__user}${isCurrentUser ? ` ${styles.winnersList__currentUser}` : ``}`}
              >
                <strong>
                  <Currency
                    locale={locale}
                    value={winner.win}
                    currency={winner.currency}
                    hideCurrency={hideCurrency}
                    avoidTrailingZero={true}
                  />
                </strong>
                <span className={styles.winnersList__user_truncate}>{winner.screenName}</span>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};
